import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type Shop = { id: number; code: string; name: string };

type CategoryStat = {
  category: string;
  count: number;
  pos_cod: number;
  bk_cod: number;
  diff: number;
};

type DayStat = {
  received_date: string;
  total: number;
  matched: number;
  cod_partial: number;
  in_progress: number;
  overdue: number;
  cod_returned: number;
  no_settlement: number;
  vtp_not_done: number;
  pos_cod: number;
  bk_cod: number;
  bk_received: number;
};

type Summary = {
  shop_id: number;
  date_from: string;
  date_to: string;
  total: number;
  by_category: CategoryStat[];
  by_date: DayStat[];
  missing_in_pos: any[];
};

type Lock = {
  id: number;
  log_date: string;
  total_orders: number | null;
  total_cod_pos: number;
  total_cod_vtp: number;
  total_settlement_received: number;
  matched_count: number | null;
  cod_mismatch_count: number | null;
  no_settlement_count: number | null;
  vtp_not_done_count: number | null;
  closed_at: string;
};

type Tab =
  | "matched"
  | "cod_partial"
  | "in_progress"
  | "overdue"
  | "cod_returned"
  | "no_settlement"
  | "vtp_not_done"
  | "missing_in_pos"
  | "orphan_return";

// Static class mapping — Tailwind needs literal class names at build time
const TAB_META: Record<
  Tab,
  { label: string; description: string; activeClass: string; labelClass: string }
> = {
  matched: {
    label: "Đã đối soát xong",
    description:
      "SUM(COD bảng kê) = POScake COD → đã nhận đủ tiền hàng (sau khi trừ phí ship).",
    activeClass: "bg-green-50 border-green-300",
    labelClass: "text-green-700",
  },
  cod_partial: {
    label: "Giao 1 phần",
    description:
      "Khách nhận 1 phần đơn hàng, phần còn lại trả về (VTP có dòng 1P1/CHPKE). Doanh thu = phần khách thực nhận. Mất phí ship cả 2 chiều.",
    activeClass: "bg-purple-50 border-purple-300",
    labelClass: "text-purple-700",
  },
  in_progress: {
    label: "Đang đối soát",
    description:
      "Mới có bảng kê trừ phí ship (≤7 ngày từ ngày giao). Đang chờ bảng kê thu COD — bình thường.",
    activeClass: "bg-blue-50 border-blue-300",
    labelClass: "text-blue-700",
  },
  overdue: {
    label: "⚠️ Trễ đối soát",
    description:
      "Đã > 7 ngày từ ngày giao thành công nhưng VTP vẫn chưa gửi bảng kê COD. NGHI VẤN sót đơn — cần liên hệ VTP để xác nhận.",
    activeClass: "bg-orange-50 border-orange-400",
    labelClass: "text-orange-700",
  },
  cod_returned: {
    label: "Đơn hoàn / lỗi",
    description:
      "VTP báo hoàn (status 504/503) HOẶC có bảng kê hoàn (BKCODRT-* / CHPKE) → đơn KHÔNG nhận tiền, mất phí ship.",
    activeClass: "bg-red-50 border-red-300",
    labelClass: "text-red-700",
  },
  no_settlement: {
    label: "Chưa có bảng kê",
    description:
      "Đơn đã giao (VTP 501) nhưng chưa có bảng kê nào (còn trong ≤7 ngày).",
    activeClass: "bg-yellow-50 border-yellow-300",
    labelClass: "text-yellow-700",
  },
  vtp_not_done: {
    label: "VTP chưa xong",
    description: "Đơn đang vận chuyển hoặc trạng thái chưa final.",
    activeClass: "bg-gray-100 border-gray-300",
    labelClass: "text-gray-700",
  },
  missing_in_pos: {
    label: "Bảng kê thừa (PKE)",
    description:
      "Bảng kê có dòng PKE mới (chưa trừ ship + COD) nhưng POScake KHÔNG có mã đơn này. Đây là đơn POScake sót → cần điều tra/sync lại.",
    activeClass: "bg-purple-50 border-purple-300",
    labelClass: "text-purple-700",
  },
  orphan_return: {
    label: "Đơn hoàn lạ",
    description:
      "Bảng kê có dòng hoàn (CHPKE/CTPPKE/1P1) nhưng POScake KHÔNG có mã đơn gốc. Hiển thị rõ phí ship + phí hoàn để hạch toán riêng.",
    activeClass: "bg-pink-50 border-pink-300",
    labelClass: "text-pink-700",
  },
};

const fmtVND = (v: number | string | null) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + "đ";

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString("vi-VN") : "-");
const fmtJustDate = (s: string | null) =>
  s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("vi-VN") : "-";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => today().slice(0, 8) + "01";

type ShopSelect = number | "all" | null;

export function ReconciliationPage() {
  const qc = useQueryClient();
  // Mặc định "all" — anh muốn nhìn toàn cục, sau đó filter từng shop
  const [shopId, setShopId] = useState<ShopSelect>("all");
  const [dateFrom, setDateFrom] = useState(startOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [tab, setTab] = useState<Tab>("overdue");

  const shopsQ = useQuery({
    queryKey: ["shops"],
    queryFn: async () => (await api.get<Shop[]>("/shops")).data,
  });

  // shopId === null  → chưa chọn (đang load)
  // shopId === "all" → tất cả shop
  // shopId === number → 1 shop cụ thể
  const isAll = shopId === ("all" as any);
  const shopParam: number | null = isAll ? null : (shopId as number | null);

  const summaryQ = useQuery({
    queryKey: ["recon-summary", shopId, dateFrom, dateTo],
    enabled: shopId !== null,
    queryFn: async () => {
      const params: any = { date_from: dateFrom, date_to: dateTo };
      if (shopParam !== null) params.shop_id = shopParam;
      const r = await api.get<Summary>("/reconciliation/summary", { params });
      return r.data;
    },
  });

  const itemsQ = useQuery({
    queryKey: ["recon-items", shopId, dateFrom, dateTo, tab],
    enabled: shopId !== null,
    queryFn: async () => {
      const params: any = {
        date_from: dateFrom,
        date_to: dateTo,
        category: tab,
        limit: 200,
      };
      if (shopParam !== null) params.shop_id = shopParam;
      const r = await api.get<{ items: any[]; total: number }>("/reconciliation/items", {
        params,
      });
      return r.data;
    },
  });

  const locksQ = useQuery({
    queryKey: ["recon-locks", shopId, dateFrom, dateTo],
    enabled: shopId !== null && !isAll, // locks là per-shop
    queryFn: async () => {
      const r = await api.get<Lock[]>("/reconciliation/locks", {
        params: { shop_id: shopParam, date_from: dateFrom, date_to: dateTo },
      });
      return r.data;
    },
  });

  const lockedDates = useMemo(
    () => new Set((locksQ.data ?? []).map((l) => l.log_date)),
    [locksQ.data]
  );

  const closeDay = useMutation({
    mutationFn: async (log_date: string) => {
      if (isAll || typeof shopId !== "number") {
        throw new Error("Chốt sổ phải chọn 1 shop cụ thể");
      }
      await api.post("/reconciliation/close-day", { shop_id: shopId, log_date });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon-locks"] });
    },
  });

  const reopenDay = useMutation({
    mutationFn: async (lockId: number) => {
      await api.delete(`/reconciliation/locks/${lockId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon-locks"] });
    },
  });

  const categoryByName = useMemo(() => {
    const m: Record<string, CategoryStat> = {};
    (summaryQ.data?.by_category ?? []).forEach((c) => (m[c.category] = c));
    return m;
  }, [summaryQ.data]);

  const missingCount = categoryByName["missing_in_pos"]?.count ?? 0;
  const orphanCount = categoryByName["orphan_return"]?.count ?? 0;
  const orphanFee = categoryByName["orphan_return"]?.bk_cod ?? 0; // bk_cod chứa fee cho orphan

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">Đối chiếu doanh thu</h1>
      <p className="text-sm text-slate-500 mb-5">POScake vs bảng kê ĐVVC vs tiền COD thực nhận.</p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Cửa hàng</label>
          <select
            value={shopId === null ? "" : String(shopId)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "all") setShopId("all");
              else setShopId(Number(v));
            }}
            className="border rounded px-2 py-1 text-sm min-w-[14rem]"
          >
            <option value="all">— Tất cả shop —</option>
            {shopsQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Từ ngày</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Đến ngày</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="text-sm text-gray-600 ml-2">
          {summaryQ.data && (
            <>
              Tổng <b>{summaryQ.data.total}</b> đơn POScake trong khoảng
            </>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {(["matched", "cod_partial", "in_progress", "overdue", "cod_returned", "no_settlement", "vtp_not_done"] as Tab[]).map((t) => {
          const c = categoryByName[t];
          const meta = TAB_META[t];
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-left p-3 rounded border ${
                tab === t ? meta.activeClass : "bg-white"
              } hover:bg-gray-50 transition`}
            >
              <div className={`text-xs ${meta.labelClass}`}>{meta.label}</div>
              <div className="text-xl font-semibold">{c?.count ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                COD POS: {fmtVND(c?.pos_cod ?? 0)}
              </div>
              <div className="text-xs text-gray-500">BK: {fmtVND(c?.bk_cod ?? 0)}</div>
            </button>
          );
        })}
        <button
          onClick={() => setTab("missing_in_pos")}
          className={`text-left p-3 rounded border ${
            tab === "missing_in_pos" ? "bg-purple-50 border-purple-300" : "bg-white"
          } hover:bg-gray-50 transition`}
        >
          <div className="text-xs text-purple-700">Bảng kê thừa (PKE)</div>
          <div className="text-xl font-semibold">{missingCount}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            POScake mất đơn — chỉ PKE pure
          </div>
        </button>
        <button
          onClick={() => setTab("orphan_return")}
          className={`text-left p-3 rounded border ${
            tab === "orphan_return" ? "bg-pink-50 border-pink-300" : "bg-white"
          } hover:bg-gray-50 transition`}
        >
          <div className="text-xs text-pink-700">Đơn hoàn lạ</div>
          <div className="text-xl font-semibold">{orphanCount}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            CHPKE/1P1 không match — phí: {fmtVND(orphanFee)}
          </div>
        </button>
      </div>

      {/* Daily roll-up */}
      <div className="mb-4 bg-white border rounded">
        <div className="px-3 py-2 text-sm font-medium border-b bg-gray-50">
          Tổng hợp theo ngày
        </div>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-2 py-1.5">Ngày</th>
              <th className="text-right px-2 py-1.5">Đơn</th>
              <th className="text-right px-2 py-1.5">Khớp</th>
              <th className="text-right px-2 py-1.5">Giao 1 phần</th>
              <th className="text-right px-2 py-1.5">Đang ĐS</th>
              <th className="text-right px-2 py-1.5">⚠ Trễ</th>
              <th className="text-right px-2 py-1.5">Hoàn</th>
              <th className="text-right px-2 py-1.5">Chưa BK</th>
              <th className="text-right px-2 py-1.5">POScake COD</th>
              <th className="text-right px-2 py-1.5">BK COD</th>
              <th className="text-right px-2 py-1.5">BK thu về</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {summaryQ.data?.by_date.map((d) => {
              const locked = lockedDates.has(d.received_date);
              const lock = locksQ.data?.find((l) => l.log_date === d.received_date);
              return (
                <tr
                  key={d.received_date}
                  className={`border-t ${locked ? "bg-green-50" : ""}`}
                >
                  <td className="px-2 py-1.5">{fmtJustDate(d.received_date)}</td>
                  <td className="px-2 py-1.5 text-right">{d.total}</td>
                  <td className="px-2 py-1.5 text-right text-green-700">{d.matched}</td>
                  <td
                    className={`px-2 py-1.5 text-right ${
                      d.cod_partial > 0 ? "text-purple-700" : "text-gray-400"
                    }`}
                  >
                    {d.cod_partial}
                  </td>
                  <td className="px-2 py-1.5 text-right text-blue-700">
                    {d.in_progress}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right ${
                      d.overdue > 0 ? "text-orange-700 font-semibold" : "text-gray-400"
                    }`}
                  >
                    {d.overdue}
                  </td>
                  <td className="px-2 py-1.5 text-right text-red-700">
                    {d.cod_returned}
                  </td>
                  <td className="px-2 py-1.5 text-right text-yellow-700">
                    {d.no_settlement}
                  </td>
                  <td className="px-2 py-1.5 text-right">{fmtVND(d.pos_cod)}</td>
                  <td className="px-2 py-1.5 text-right">{fmtVND(d.bk_cod)}</td>
                  <td className="px-2 py-1.5 text-right font-medium">
                    {fmtVND(d.bk_received)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {locked && lock ? (
                      <button
                        onClick={() => reopenDay.mutate(lock.id)}
                        className="text-xs text-orange-600 hover:underline"
                      >
                        🔒 Mở lại
                      </button>
                    ) : (
                      <button
                        onClick={() => closeDay.mutate(d.received_date)}
                        disabled={closeDay.isPending}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Chốt sổ
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {summaryQ.data?.by_date.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-gray-500 px-2 py-4">
                  Không có dữ liệu trong khoảng này.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tab description */}
      <div className="mb-2 text-sm text-gray-600">
        <span className="font-medium">{TAB_META[tab].label}:</span>{" "}
        {TAB_META[tab].description}
      </div>

      {/* Items table */}
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            {tab === "missing_in_pos" ? (
              <tr>
                <th className="text-left px-2 py-1.5">Mã đơn</th>
                <th className="text-right px-2 py-1.5">BK COD</th>
                <th className="text-right px-2 py-1.5">BK cước</th>
                <th className="text-right px-2 py-1.5">BK thu về</th>
                <th className="text-left px-2 py-1.5">Bảng kê</th>
                <th className="text-left px-2 py-1.5">Ngày đối soát</th>
                <th className="text-left px-2 py-1.5">Ngày phát</th>
              </tr>
            ) : tab === "orphan_return" ? (
              <tr>
                <th className="text-left px-2 py-1.5">Mã vận đơn gốc</th>
                <th className="text-left px-2 py-1.5">Các mã trong BK</th>
                <th className="text-right px-2 py-1.5">Phí ship</th>
                <th className="text-right px-2 py-1.5">Phí hoàn</th>
                <th className="text-right px-2 py-1.5">Tổng phí</th>
                <th className="text-right px-2 py-1.5">COD</th>
                <th className="text-right px-2 py-1.5">Thực thu</th>
                <th className="text-left px-2 py-1.5">Bảng kê</th>
                <th className="text-left px-2 py-1.5">Ngày ĐS</th>
              </tr>
            ) : (
              <tr>
                <th className="text-left px-2 py-1.5">Mã đơn</th>
                <th className="text-left px-2 py-1.5">Ngày nhận</th>
                {isAll && <th className="text-left px-2 py-1.5">Shop</th>}
                <th className="text-left px-2 py-1.5">Khách</th>
                <th className="text-right px-2 py-1.5">POS COD</th>
                <th className="text-right px-2 py-1.5">BK COD</th>
                <th className="text-right px-2 py-1.5">Lệch</th>
                <th className="text-right px-2 py-1.5">Trễ (ngày)</th>
                <th className="text-left px-2 py-1.5">VTP status</th>
                <th className="text-left px-2 py-1.5">Bảng kê</th>
              </tr>
            )}
          </thead>
          <tbody>
            {itemsQ.isLoading && (
              <tr>
                <td colSpan={9} className="text-center text-gray-500 px-2 py-4">
                  Đang tải...
                </td>
              </tr>
            )}
            {itemsQ.data?.items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-gray-500 px-2 py-4">
                  Không có đơn nào trong nhóm này.
                </td>
              </tr>
            )}
            {tab === "missing_in_pos"
              ? itemsQ.data?.items.map((it: any, i: number) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-1.5 font-mono">{it.order_code}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVND(it.bk_cod)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVND(it.bk_fee)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVND(it.bk_received)}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {it.settlement_code}
                    </td>
                    <td className="px-2 py-1.5">{fmtJustDate(it.settlement_date)}</td>
                    <td className="px-2 py-1.5">{fmtDate(it.deliver_date)}</td>
                  </tr>
                ))
              : tab === "orphan_return"
              ? itemsQ.data?.items.map((it: any, i: number) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-1.5 font-mono">{it.order_code}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600 max-w-[14rem] truncate">
                      {it.all_codes}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-600">
                      {fmtVND(it.ship_fee)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-red-700 font-medium">
                      {fmtVND(it.return_fee)}
                    </td>
                    <td className="px-2 py-1.5 text-right">{fmtVND(it.bk_fee)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVND(it.bk_cod)}</td>
                    <td
                      className={`px-2 py-1.5 text-right ${
                        it.bk_received < 0 ? "text-red-600" : ""
                      }`}
                    >
                      {fmtVND(it.bk_received)}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">
                      {it.settlement_code}
                    </td>
                    <td className="px-2 py-1.5">{fmtJustDate(it.settlement_date)}</td>
                  </tr>
                ))
              : itemsQ.data?.items.map((it: any) => {
                  const diff =
                    it.bk_cod !== null ? Number(it.bk_cod) - Number(it.pos_cod) : null;
                  const shopCode =
                    shopsQ.data?.find((s) => s.id === it.shop_id)?.code ??
                    (it.shop_id ? `#${it.shop_id}` : "—");
                  return (
                    <tr key={it.pos_id} className="border-t hover:bg-gray-50">
                      <td className="px-2 py-1.5 font-mono">{it.order_code}</td>
                      <td className="px-2 py-1.5">{fmtJustDate(it.received_date)}</td>
                      {isAll && (
                        <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">
                          {shopCode}
                        </td>
                      )}
                      <td className="px-2 py-1.5 truncate max-w-[10rem]">
                        {it.customer_name}
                      </td>
                      <td className="px-2 py-1.5 text-right">{fmtVND(it.pos_cod)}</td>
                      <td className="px-2 py-1.5 text-right">
                        {it.bk_cod === null ? "—" : fmtVND(it.bk_cod)}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right ${
                          diff !== null && Math.abs(diff) > 0.5
                            ? "text-red-600 font-medium"
                            : "text-gray-400"
                        }`}
                      >
                        {diff === null ? "—" : fmtVND(diff)}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right ${
                          it.days_since_delivery !== null && it.days_since_delivery > 7
                            ? "text-orange-700 font-semibold"
                            : it.days_since_delivery !== null && it.days_since_delivery > 3
                            ? "text-yellow-700"
                            : "text-gray-500"
                        }`}
                      >
                        {it.days_since_delivery === null ? "—" : `${it.days_since_delivery}d`}
                      </td>
                      <td className="px-2 py-1.5">
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${
                            it.vtp_status === 501
                              ? "bg-green-100 text-green-800"
                              : it.vtp_status === 504
                              ? "bg-orange-100 text-orange-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {it.vtp_status ?? "?"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">
                        {it.bk_settlement_code ?? "—"}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {itemsQ.data && itemsQ.data.total > itemsQ.data.items.length && (
          <div className="p-2 text-xs text-gray-500 border-t">
            Hiển thị {itemsQ.data.items.length} / {itemsQ.data.total} đơn (chỉnh limit qua API
            nếu cần xem hết)
          </div>
        )}
      </div>

      {(closeDay.error || reopenDay.error) && (
        <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
          {((closeDay.error || reopenDay.error) as any)?.response?.data?.detail ??
            String(closeDay.error || reopenDay.error)}
        </div>
      )}
    </div>
  );
}
